import React, {FC} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import * as yup from 'yup';
import {
    Form,
    Formik,
} from 'formik';
import confirm from '@touchnet/react-confirm-modal';

import FieldRow from '../forms/field-row';
import FormActionButtons from '../forms/form-action-buttons';
import withCommonWorktags from '../../providers/with-common-worktags';
import withOneCode from '../../providers/with-one-code';
import withCodes from '../../providers/with-codes';
import Selectors from "~/state/selectors.ts";
import {NavigateFunction} from "react-router-dom";
import {actions} from "~/state/reducers/codes-reducer.ts";
import {withRouter} from "~/providers/with-router.tsx";

interface ComponentProps {
    navigate: NavigateFunction;
}

const CodesInfoForm: FC<ComponentProps> = ({navigate}: ComponentProps) => {
    const dispatch = useDispatch();
    const code = useSelector(Selectors.getCurrentCode);
    const existingCodes = useSelector(Selectors.getCodes);

    const onSubmit = (values, {setSubmitting}) => {
        // Merge and Unflatten Data
        const adjustedValues = {
            id: code.id,
            name: values.name,
            enabled: code.enabled,
            lineItemId: code.lineItemId,
            worktags: [],
        };

        // eslint-disable-next-line no-warning-comments
        // @TODO Company and Account ID have nowhere to go yet.

        adjustedValues.worktags = code.worktags.map(worktag => ({
            id: worktag.id,
            name: worktag.name,
            value: values[`_${worktag.name}`],
            required: worktag.required,
            editable: worktag.editable,
        }));
        const successMessage: string = code.id === '' ? 'Accounting Code Created' : 'Accounting Code Updated';
        dispatch(actions.updateOneCode({adjustedValues, navigate, successMessage}))
        setSubmitting(false);
    };

    const resetForm = async (e, formikProps) => {
        e.preventDefault();
        e.stopPropagation();

        const {
            dirty,
            handleReset,
        } = formikProps;

        if (dirty) {
           await confirm({
                cancelLabel: 'Never mind',
                children: 'You have unsaved changes. Are you sure you want to discard them?',
                okLabel: 'Yes, I\'m sure',
                title: 'Are you sure?',
            }).then(handleReset).catch( () => {});
        }
    };

    const initialValues = {
        id: code.id,
        name: code.name,
        company: code.company,
        accountId: code.accountId,
    };

    const codeSchema = {
        name: yup.string()
            .required('Accounting Code Name is Requried')
            .test('unique', 'Account Code Name must be Unique', value => {
                if (code.id !== '' && value === code.name) {
                    return true;
                }
                return 0 === existingCodes.filter(item => value === item.name).length;
            }),
        company: yup.string()
            .required('Company Name is Required for Validation Purposes'),
        accountId: yup.string()
            .required('Account Id is Requried for Validation Purposes'),
    };

    const sortedWorktags = [...code.worktags];
    sortedWorktags.sort((foo, bar) => (foo.name < bar.name ? -1 : 1));
    sortedWorktags.forEach(commonWorktag => {
        initialValues[`_${commonWorktag.name}`] = commonWorktag.value;
        if (commonWorktag.required) {
            codeSchema[`_${commonWorktag.name}`] = yup.string().required(`${commonWorktag.name} is Required`);
        }
    });

    const validationSchema = yup.object().shape(codeSchema);

    return (
        <Formik
            validateOnBlur
            key={initialValues.id}
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
            >
            {formikProps => {
                const {
                    dirty,
                    isSubmitting,
                    status,
                } = formikProps;

                const preventSubmit = !dirty || isSubmitting;

                const formButtonProps = {
                    reset: {
                        handler: e => resetForm(e, formikProps),
                        disabled: !dirty || isSubmitting,
                    },
                    submit: {disabled: preventSubmit},
                };

                return (
                    <Form onSubmit={formikProps.handleSubmit}>
                        {status?.msg && (<div>{status.msg}</div>)}

                        <FormActionButtons {...formButtonProps} />
                        <h3 className="mb-3">
                            Accounting Code
                        </h3>

                        <FieldRow
                            fieldName="name"
                            label="Accounting Code Name"
                            {...formikProps}
                        />
                        <FieldRow
                            fieldName="company"
                            label="Company"
                            helpText="For Validation Only - will not be saved with Accounting Code record"
                            {...formikProps}
                        />
                        <FieldRow
                            fieldName="accountId"
                            label="Bank Account Id"
                            helpText="For Validation Only - will not be saved with Accounting Code record"
                            {...formikProps}
                        />
                        <div className="my-3">
                            <h3>Common Worktags</h3>
                        </div>
                        {sortedWorktags.map(worktag => (
                            <FieldRow
                                optional={!worktag.required}
                                key={`_${worktag.name}`}
                                fieldName={`_${worktag.name}`}
                                label={worktag.name}
                                {...formikProps}
                            />
                        ))}
                    </Form>
                );
            }}
        </Formik>
    );
}

export default withRouter(withCodes(withCommonWorktags(withOneCode(CodesInfoForm))));
